import React, { useEffect, useMemo, useCallback } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { Requisition } from '../../types';
import Button from './Button';
import Input from './Input';
import Select from './Select';
import { useBranches } from '../../hooks/useBranches';
import { useCodePrefixes } from '../../hooks/useCodePrefixes';
import { useRequisitions } from '../../hooks/useRequisitions';
import { useProviders } from '../../hooks/useProviders';
import RequisitionItemsTable from './RequisitionItemsTable';
import RequisitionStatusSection from './RequisitionStatusSection';

const EMPTY_REQ_ITEM = {
  // id left empty so submit assigns a unique one; a fixed "1" collides across requisitions
  id: '', itemNumber: 1, item: '', qty: 0, rob: '', appQty: 0, unitPrice: 0,
  supplier: '', remarks: '', status: 'pending' as const, selected: false,
};

/** "E" + 3 -> "E-Req-03-2026"; trailing dashes on the prefix are stripped so
 *  a prefix like "2026-" doesn't produce "2026--Req-..." */
const generateFormNumber = (prefix: string, sequence: number): string => {
  const year = new Date().getFullYear();
  const clean = (prefix || '').replace(/-+$/, '');
  return `${clean}-Req-${sequence.toString().padStart(2, '0')}-${year}`;
};

interface RequisitionFormProps {
  requisition?: Requisition;
  onSubmit: (data: Partial<Requisition>) => void;
  onCancel: () => void;
  userRole?: 'vessel' | 'office';
}

const departmentOptions = ['DECK', 'ENGINE', 'CATERING', 'SAFETY', 'OFFICE', 'GENERAL'].map(d => ({ value: d, label: d }));

const RequisitionForm: React.FC<RequisitionFormProps> = ({ 
  requisition, 
  onSubmit, 
  onCancel,
  userRole = 'office'
}) => {
  const { data: branchesData } = useBranches();
  const codePrefixQueryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 100 }
  }), []);
  const { data: codePrefixes = [] } = useCodePrefixes(codePrefixQueryOptions);
  const { data: existingReqs = [] } = useRequisitions();
  const { data: providersData = [] } = useProviders();

  const supplierOptions = useMemo(() =>
    (providersData as any[])
      .filter((p: any) => p.name)
      .sort((a: any, b: any) => String(a.name).localeCompare(String(b.name)))
      .map((p: any) => ({ value: p.name, label: p.name })),
    [providersData]);

  // Requisition numbering is independent per prefix: next number = highest
  // sequence already used by a requisition with that prefix + 1.
  // excludeId skips the requisition being edited so its own number doesn't count.
  const nextReqNumber = useCallback((prefix: string, excludeId?: string) => {
    const clean = (prefix || '').replace(/-+$/, '');
    let maxSeq = 0;
    (existingReqs as any[]).forEach((r: any) => {
      if (excludeId && (r.documentId === excludeId || String(r.id) === excludeId)) return;
      const samePrefix = r.prefix === prefix ||
        (r.formNumber || '').replace(/-+/g, '-').startsWith(`${clean}-Req-`);
      if (!samePrefix) return;
      const m = /Req-(\d+)/i.exec(r.formNumber || '');
      if (m) maxSeq = Math.max(maxSeq, parseInt(m[1], 10));
    });
    return generateFormNumber(prefix, maxSeq + 1);
  }, [existingReqs]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    watch,
    setValue
  } = useForm<Requisition>({
    defaultValues: requisition ? {
      ...requisition,
      // Date inputs need plain YYYY-MM-DD values; old reqs may have no items yet
      requestDate: (requisition.requestDate || '').split('T')[0],
      requiredDeliveryDate: (requisition.requiredDeliveryDate || '').split('T')[0],
      dateOfIssue: (requisition.dateOfIssue || '').split('T')[0],
      dateOfRevision: (requisition.dateOfRevision || '').split('T')[0],
      items: Array.isArray(requisition.items) && requisition.items.length > 0
        ? requisition.items
        : [{ ...EMPTY_REQ_ITEM }],
    } : {
      prefix: 'E',
      formNumber: generateFormNumber('E', 1),
      title: 'GOODS AND SERVICE SUPPLY REQUEST',
      vessel: '',
      location: '',
      department: '',
      requestDate: new Date().toISOString().split('T')[0],
      requiredDeliveryDate: '',
      items: [{ ...EMPTY_REQ_ITEM }],
      chiefOfficerSignature: '',
      chiefOfficerName: '',
      captainSignature: '',
      captainName: '',
      vesselManagerSignature: '',
      vesselManagerName: '',
      dateOfIssue: new Date().toISOString().split('T')[0],
      revisionStatus: '00',
      dateOfRevision: '',
      pageNumber: '1 of 1',
      status: 'pending',
      preparedBy: '',
      notes: ''
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items'
  });

  const watchedPrefix = watch('prefix');
  
  useEffect(() => {
    if (!watchedPrefix) return;
    // Editing keeps the saved number while the prefix is unchanged; a changed
    // prefix renumbers under the new prefix (excluding this requisition itself)
    if (!requisition) setValue('formNumber', nextReqNumber(watchedPrefix));
    else if (watchedPrefix === requisition.prefix) setValue('formNumber', requisition.formNumber);
    else setValue('formNumber', nextReqNumber(watchedPrefix, requisition.documentId || String(requisition.id)));
  }, [watchedPrefix, requisition, setValue, nextReqNumber]);

  // Both vessels and headquarters can be selected as the requisition location
  const vesselOptions = useMemo(() => {
    if (!branchesData || branchesData.length === 0) return [];
    return branchesData
      .filter((branch: any) => branch.name)
      .map((branch: any) => ({
        value: branch.name,
        label: branch.type === 'vessel' ? branch.name : `${branch.name} (HQ)`
      }));
  }, [branchesData]);

  const onFormSubmit = (data: Requisition) => {
    const processedData = {
      ...data,
      items: data.items.map((item, index) => ({
        ...item,
        id: item.id || Date.now().toString() + index,
        itemNumber: index + 1,
        // Inputs register values as strings; store real numbers in the JSON
        qty: Number(item.qty) || 0,
        appQty: Number(item.appQty) || 0,
        unitPrice: Number(item.unitPrice) || 0,
      }))
    };
    onSubmit(processedData);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {requisition ? 'Edit Requisition' : 'Create Requisition'}
          </h1>
          <p className="text-gray-600">
            {requisition ? 'Update requisition details' : 'Create a new goods and service supply request'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-8">
        {/* Header Section */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">PETROMARINE OFFSHORE PROJECTS SUPPORT CO.</h2>
            <p className="text-sm text-gray-600">SAFETY MANAGEMENT SYSTEM (S.M.S.)</p>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <Select
                  label="Department Prefix"
                  {...register('prefix', { required: 'Prefix is required' })}
                  error={errors.prefix?.message}
                  options={(codePrefixes as any[]).filter((p: any) => p.isActive).map((p: any) => ({
                    value: p.prefix,
                    label: `${p.prefix} - ${p.description || p.name || ''}`
                  }))}
                />
              </div>
              <div>
                <Input
                  label="Form Number"
                  {...register('formNumber', { required: 'Form number is required' })}
                  error={errors.formNumber?.message}
                  disabled
                />
              </div>
              <div>
                <Input
                  label="Title"
                  {...register('title', { required: 'Title is required' })}
                  error={errors.title?.message}
                />
              </div>
              <div>
                <Input
                  label="Page Number"
                  {...register('pageNumber')}
                  placeholder="e.g., 1 of 1"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Vessel and Location Information */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">Vessel & Location Information</h2>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <Select
                  label="Vessel"
                  {...register('vessel', { required: 'Vessel is required' })}
                  options={vesselOptions}
                  error={errors.vessel?.message}
                />
              </div>
              <div>
                <Input
                  label="Location"
                  {...register('location', { required: 'Location is required' })}
                  error={errors.location?.message}
                  placeholder="Current vessel location"
                />
              </div>
              <div>
                <Select
                  label="Department"
                  {...register('department', { required: 'Department is required' })}
                  options={departmentOptions}
                  error={errors.department?.message}
                />
              </div>
              <div>
                <Input
                  label="Request Date"
                  type="date"
                  {...register('requestDate', { required: 'Request date is required' })}
                  error={errors.requestDate?.message}
                />
              </div>
              <div>
                <Input
                  label="Required Delivery Date"
                  type="date"
                  {...register('requiredDeliveryDate', { required: 'Required delivery date is required' })}
                  error={errors.requiredDeliveryDate?.message}
                />
              </div>
            </div>
          </div>
        </div>

        <RequisitionItemsTable
          register={register}
          fields={fields}
          append={append}
          remove={remove}
          setValue={setValue}
          userRole={userRole}
          supplierOptions={supplierOptions}
        />

        <RequisitionStatusSection register={register} errors={errors} />

        {/* Form Actions */}
        <div className="flex justify-end space-x-4">
          <Button type="button" onClick={onCancel} variant="secondary">
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : requisition ? 'Update Requisition' : 'Create Requisition'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default RequisitionForm;
